import classNames from 'classnames';
import BAlert from 'components/common/BAlert';
import BTabs from 'components/common/BTabs';
import ModalWithActivator from 'components/common/ModalWithActivator';
import BDatePicker from 'components/Form/BDatePicker';
import BStepperInput from 'components/Form/BStepperInput';
import BSwitch from 'components/Form/BSwitch';
import { BusinessModel } from 'enums/businessModel';
import { Currency } from 'enums/currency';
import { SupportedDemandType } from 'enums/SupportedDemandType';
import { VehicleType } from 'enums/vehicleTypes';
import { usePriceWidgetCalculateAmounts } from 'hooks/priceWidget/usePriceWidgetCalculateAmounts';
import { usePriceWidgetClosestAvailability } from 'hooks/priceWidget/usePriceWidgetClosestAvailability';
import { usePriceWidgetDisabledHours } from 'hooks/priceWidget/usePriceWidgetDisabledHours';
import { usePriceWidgetDurationLimits } from 'hooks/priceWidget/usePriceWidgetDurationLimits';
import { usePriceWidgetExtraSections } from 'hooks/priceWidget/usePriceWidgetExtraSections';
import { usePriceWidgetHarborIntersections } from 'hooks/priceWidget/usePriceWidgetHarborIntersections';
import { usePriceWidgetPeopleLimits } from 'hooks/priceWidget/usePriceWidgetPeopleLimits';
import { usePriceWidgetReservedTimes } from 'hooks/priceWidget/usePriceWidgetReservedTimes';
import { usePriceWidgetSwimmingTour } from 'hooks/priceWidget/usePriceWidgetSwimmingTour';
import { useForm } from 'hooks/useForm';
import { useTranslatedNavigation } from 'hooks/useTranslatedNavigation';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { analyticsService } from 'services/AnalyticsService';
import { dateService } from 'services/DateService';
import { demandService } from 'services/DemandService';
import {
  CalculatePricing,
  DemandResponseDemand,
  DemandSubmitType,
  SelectedExtraUnits,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { DropdownOption } from 'types/types';
import {
  Extra,
  ExtraWithUnit,
  HourlyRentalInfo,
  ReservedTime,
} from 'types/vehicle.type';
import { vehicleBasedValidation } from 'validation/demand.validation';
import PriceWidgetCTAButton from './PriceWidgetCTAButton';
import PriceWidgetExtras from './PriceWidgetExtras';
import PriceWidgetExtrasEmptyView from './PriceWidgetExtrasEmptyView';
import selectStyles from 'styles/BSelect.module.css';
import { eventBus } from 'services/EventBus';
import { useQueryParams } from 'hooks/useQueryParams';
import { usePriceWidgetIsSpecialDate } from 'hooks/priceWidget/usePriceWidgetIsSpecialDate';
import { useAuthCheck } from 'hooks/useAuthCheck';
import { VehiclePriceUnit } from 'enums/vehiclePriceUnit';
// import equal from 'fast-deep-equal/react';
import BButton from 'components/common/BButton';
import BTextArea from 'components/Form/BTextArea';
import BSpinnerInline from 'components/common/BSpinnerInline';
import { SupportedHourlyRentalTypes } from 'enums/supportedRentalTypes';
import { useRemoteConfigState } from 'states/useRemoteConfigState';
import { getLocalePrefix } from 'utils/getLocalePrefix';
import { useLocale } from 'hooks/useLocale';
import { Info } from '@phosphor-icons/react';

export interface PriceWidgetFormProps {
  supportedServices: ExtraWithUnit[];
  supportedPackages: ExtraWithUnit[];
  supportedMeals: Extra[];
  fullCapacity: number;
  diningCapacity: number;
  vehicleId: number;
  setPricing: (val: CalculatePricing.MappedResponseWithDuration) => void;
  rentalInfo: HourlyRentalInfo;
  vehicleTimeZone: string;
  reservedTimes: ReservedTime[];
  harbors: DropdownOption<number>[];
  originHarborId: number;
  supportedDemandTypes: SupportedDemandType[];
  pricing?: CalculatePricing.MappedResponseWithDuration;
  isCalculateAmountRequestSent: boolean;
  setIsCalculateAmountRequestSent: (val: boolean) => void;
  businessModel: BusinessModel;
  vehicleName: string;
  vehicleType: VehicleType;
  regionTRName: string;
  isCalcutePricingRequestOngoing: boolean;
  setIsCalcutePricingRequestOngoing: (val: boolean) => void;
  submitType?: DemandSubmitType;
  demandId?: number;
  submitCallback?: () => void;
  demandData?: DemandResponseDemand;
}

const PriceWidgetForm: FunctionComponent<PriceWidgetFormProps> = ({
  supportedServices,
  supportedPackages,
  supportedMeals,
  fullCapacity,
  diningCapacity,
  vehicleId,
  setPricing,
  rentalInfo,
  reservedTimes,
  harbors,
  originHarborId,
  supportedDemandTypes,
  pricing,
  isCalculateAmountRequestSent,
  setIsCalculateAmountRequestSent,
  vehicleName,
  vehicleType,
  regionTRName,
  isCalcutePricingRequestOngoing,
  setIsCalcutePricingRequestOngoing,
  submitType = 'default',
  demandId = null,
  submitCallback,
  demandData,
}) => {
  const dayjs = dateService.getDayjs();
  const router = useRouter();
  const { translateAndNavigate } = useTranslatedNavigation();
  const { t } = useTranslation('vehicle');
  const { locale } = useLocale();
  const { addQueryParam, getQueryParam } = useQueryParams();
  const { getIsSpecialDate } = usePriceWidgetIsSpecialDate(rentalInfo);
  const { checkLoggedIn } = useAuthCheck();
  const [message, setMessage] = useState<string>('');
  const { getConfig } = useRemoteConfigState();
  const isMessagingActive = getConfig('MESSAGING_ENABLED', true);

  const [isDemandRequestOngoing, setIsDemandRequestOngoing] =
    useState<boolean>(false);

  const validateForm = (
    state: VehicleBasedDemandState,
    showError: boolean
  ): Promise<boolean> => {
    return vehicleBasedValidation(
      state,
      MIN_DURATION,
      MAX_DURATION,
      fullCapacity,
      diningCapacity,
      t,
      showError
    );
  };

  const { debouncedHandleCalculateAmounts } = usePriceWidgetCalculateAmounts(
    vehicleId,
    supportedServices,
    supportedPackages,
    setPricing,
    supportedDemandTypes,
    validateForm,
    setIsCalculateAmountRequestSent,
    setIsCalcutePricingRequestOngoing,
    pricing
  );

  const generateSelectedExtraUnits = (supportedExtras: ExtraWithUnit[]) => {
    return supportedExtras.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: null,
      }),
      {} as SelectedExtraUnits
    );
  };

  const getDefaultState = useCallback((): VehicleBasedDemandState => {
    const demandState = {
      date: demandData?.startTime,
      duration:
        demandData?.startTime && demandData?.endTime
          ? dateService.getHourDuration(
              new Date(demandData.startTime),
              new Date(demandData.endTime)
            )
          : null,
      harbor:
        '' + harbors.find((h) => h.label === demandData?.startHarbor)?.value,
      endHarbor:
        '' + harbors.find((h) => h.label === demandData?.finishHarbor)?.value,
      numberOfPeople: demandData?.passengerCount
        ? '' + demandData.passengerCount
        : undefined,
      selectedMeal: demandData?.meals?.[0]?.id,
      selectedPackage: demandData?.packages?.[0]?.id,
      selectedServices: demandData?.services.map((s) => s.id),
      isSwimmingTour:
        demandData?.hourlyVariant === SupportedHourlyRentalTypes.SWIMMING_TOUR,
    };
    const queryParamState = {
      date: getQueryParam('date') as string | undefined,
      duration:
        demandState.duration ||
        (getQueryParam('duration') as string | undefined),
      harbor:
        demandState.harbor || (getQueryParam('harbor') as string | undefined),
      endHarbor:
        demandState.endHarbor ||
        (getQueryParam('endHarbor') as string | undefined),
      numberOfPeople:
        demandState.numberOfPeople ||
        (getQueryParam('numberOfPeople') as string | undefined),
      selectedMeal:
        demandState.selectedMeal ||
        (getQueryParam('selectedMeal') as string | undefined),
      selectedPackage:
        demandState.selectedPackage ||
        (getQueryParam('selectedPackage') as string | undefined),
      selectedServices:
        demandState.selectedServices ||
        (getQueryParam('selectedServices') as string[] | undefined),
      isSwimmingTour:
        demandState.isSwimmingTour ||
        (getQueryParam('isSwimmingTour') as string | undefined),
      fromLocation: getQueryParam('fromLocation') as string | undefined,
    };

    const date = dayjs(
      demandData?.startTime
        ? demandData.startTime
        : queryParamState.date
        ? +queryParamState.date
        : undefined
    )
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();

    const isDateSpecialDate = getIsSpecialDate(date);
    const minRentalDur = isDateSpecialDate
      ? rentalInfo.minimumRentDurationOnSpecialDays
      : rentalInfo.minimumRentDuration;

    const isQueryParamDurationValid =
      queryParamState.duration && minRentalDur <= +queryParamState.duration;

    const duration = isQueryParamDurationValid
      ? +queryParamState.duration!
      : minRentalDur;

    let harbor =
      queryParamState.harbor &&
      !!harbors.find((h) => h.value === +queryParamState.harbor!)
        ? queryParamState.harbor
        : `${originHarborId}`;

    let endHarbor =
      queryParamState.endHarbor &&
      !!harbors.find((h) => h.value === +queryParamState.endHarbor!)
        ? queryParamState.endHarbor
        : `${originHarborId}`;

    if (queryParamState.fromLocation) {
      const fromHarbor = harbors.find(
        (h) => h.label === queryParamState.fromLocation
      );
      if (fromHarbor) {
        harbor = `${fromHarbor.value}`;
        endHarbor = `${fromHarbor.value}`;
      }
    }

    const selectedMeal =
      queryParamState.selectedMeal &&
      !!supportedMeals.find((m) => m.id === +queryParamState.selectedMeal!)
        ? +queryParamState.selectedMeal
        : null;

    const vehicleCapacity =
      selectedMeal !== null ? diningCapacity : fullCapacity;

    const numberOfPeople =
      queryParamState.numberOfPeople &&
      +queryParamState.numberOfPeople > 0 &&
      +queryParamState.numberOfPeople <= vehicleCapacity
        ? queryParamState.numberOfPeople
        : '0';

    const selectedPackage =
      queryParamState.selectedPackage &&
      !!supportedPackages.find(
        (m) => m.id === +queryParamState.selectedPackage!
      )
        ? +queryParamState.selectedPackage
        : null;

    const selectedServices: number[] = [];

    if (
      queryParamState.selectedServices &&
      queryParamState.selectedServices.length > 0
    ) {
      queryParamState.selectedServices.forEach((s) => {
        if (supportedServices.find((ss) => ss.id === +s)) {
          selectedServices.push(+s);
        }
      });
    }

    return {
      date,
      duration,
      harbor,
      endHarbor,
      numberOfPeople,
      selectedMeal,
      selectedPackage,
      selectedServices,
      selectedMealsUnits: {},
      selectedPackagesUnits: generateSelectedExtraUnits(supportedPackages),
      selectedServicesUnits: generateSelectedExtraUnits(supportedServices),
      isSwimmingTour:
        queryParamState.isSwimmingTour === 'true' &&
        rentalInfo.supportedRentalTypes.includes(
          SupportedHourlyRentalTypes.SWIMMING_TOUR
        ),
    };
  }, []);

  const dispatchInterceptor = (state: VehicleBasedDemandState) => {
    debouncedHandleCalculateAmounts(state);
    // setStoredState(state);

    const strippedState = {
      date: state.date?.getTime(),
      duration: state.duration,
      harbor: state.harbor,
      endHarbor: state.endHarbor,
      numberOfPeople: state.numberOfPeople,
      selectedMeal: state.selectedMeal,
      selectedPackage: state.selectedPackage,
      selectedServices: state.selectedServices,
      isSwimmingTour: state.isSwimmingTour,
    };
    submitType !== 'message-update' && addQueryParam(strippedState);
  };

  const [state, dispatch] = useForm(
    {
      ...getDefaultState(),
      // ...storedState,
    },
    dispatchInterceptor
  );

  // useEffect(() => {
  //   if (storedState !== null && !equal(storedState, state)) {
  //     dispatch({
  //       payload: storedState,
  //       type: 'REPLACE_STATE',
  //     });
  //   }
  // }, [storedState, state]);

  const {
    disabledHoursWhenSwimmingTourSelected,
    doesVehicleNotSupportSwimmingTour,
    doesVehicleSupportOnlySwimmingTour,
  } = usePriceWidgetSwimmingTour(state, dispatch, rentalInfo);

  const { mappedReservedTimes } = usePriceWidgetReservedTimes(
    state,
    reservedTimes,
    rentalInfo
  );

  const { datePickerDisabledHours } = usePriceWidgetDisabledHours(
    state,
    supportedDemandTypes,
    rentalInfo
  );

  const { isDateAvailable } = usePriceWidgetClosestAvailability(
    state,
    dispatch,
    mappedReservedTimes,
    datePickerDisabledHours,
    demandData
  );

  const { lockHarborsOtherThanThis } = usePriceWidgetHarborIntersections(
    state,
    dispatch,
    mappedReservedTimes
  );

  const { MAX_PEOPLE, MIN_PEOPLE } = usePriceWidgetPeopleLimits(
    state,
    diningCapacity,
    fullCapacity
  );

  const { MAX_DURATION, MIN_DURATION } = usePriceWidgetDurationLimits(
    state,
    dispatch,
    rentalInfo,
    mappedReservedTimes,
    datePickerDisabledHours,
    demandData
  );

  const { extraTabs } = usePriceWidgetExtraSections(
    state,
    dispatch,
    supportedMeals,
    supportedPackages,
    supportedServices,
    diningCapacity
  );

  const clearUnsupportedExtras = () => {
    const statePackage = state['selectedPackage'];
    const stateServices = state['selectedServices'];
    const stateMeal = state['selectedMeal'];

    const selectedPackage = supportedPackages.some((p) => p.id === statePackage)
      ? statePackage
      : null;
    const selectedServices = stateServices.filter((service) =>
      supportedServices.some(
        (supportedService) => supportedService.id === service
      )
    );
    const selectedMeal = supportedMeals.some((m) => m.id === stateMeal)
      ? stateMeal
      : null;

    return {
      selectedPackage,
      selectedServices,
      selectedMeal,
    };
  };

  const onSubmit = async () => {
    if (!checkLoggedIn()) {
      return;
    }

    if (state.date && !isDateAvailable(state.date)) {
      toast.error(t('priceWidget.form.errors.dateNotValid'), {
        toastId: 'noValidDate',
      });
      return false;
    }

    const clearedExtras = clearUnsupportedExtras();
    const filteredState: VehicleBasedDemandState = {
      ...state,
      selectedPackage: clearedExtras.selectedPackage,
      selectedServices: clearedExtras.selectedServices,
      selectedMeal: clearedExtras.selectedMeal,
    };

    const isValid = await validateForm(filteredState, true);
    if (!isValid) return false;

    try {
      setIsDemandRequestOngoing(true);

      const payload = demandService.formatVehicleBasedDemandStateToDemand(
        filteredState,
        vehicleId,
        [...supportedServices, ...supportedPackages],
        pricing
      );

      if (submitType === 'default') {
        const res = await demandService.createDemand({
          ...payload,
          source: 'API',
        });

        analyticsService.addToCartVehicleBased({
          vehicleId,
          totalPrice: payload.paymentInfo?.totalAmount ?? 0,
          currency: payload.paymentInfo?.currency ?? Currency.TRY,
          duration: filteredState.duration,
          hourlyPrice: payload.paymentInfo?.vehicleCostPerUnit ?? 0,
          region: regionTRName,
          vehicleName,
          vehiclePriceUnit: filteredState.isSwimmingTour
            ? 'SWIMMING_TOUR'
            : payload.paymentInfo?.priceUnit || VehiclePriceUnit.HOUR,
          vehicleType,
        });

        if (res.offerId) {
          window.open(
            `${getLocalePrefix(locale)}payment/${res.offerId}`,
            '_self'
          );
        } else {
          toast.success(t('priceWidget.demandSuccess'), {
            toastId: 'demandSuccess',
          });

          if (
            isMessagingActive &&
            vehicleType === VehicleType.INVITATION_BOAT
          ) {
            translateAndNavigate(`/message?demandId=${res.demandId}`);
          } else {
            translateAndNavigate('/tekliflerim');
          }
        }
      } else if (submitType === 'message') {
        const res = await demandService
          .createDemand(
            {
              ...payload,
              initialMessage: message,
              source: 'MESSAGE',
            },
            {
              hideError: false,
            }
          )
          .catch();

        analyticsService.addToCartVehicleBased({
          vehicleId,
          totalPrice: payload.paymentInfo?.totalAmount ?? 0,
          currency: payload.paymentInfo?.currency ?? Currency.TRY,
          duration: filteredState.duration,
          hourlyPrice: payload.paymentInfo?.vehicleCostPerUnit ?? 0,
          region: regionTRName,
          vehicleName,
          vehiclePriceUnit: filteredState.isSwimmingTour
            ? 'SWIMMING_TOUR'
            : payload.paymentInfo?.priceUnit || VehiclePriceUnit.HOUR,
          vehicleType,
        });

        router.push(`/message?demandId=${res.demandId}`);
      } else if (submitType === 'message-update' && demandId) {
        await demandService.updateDemand(demandId, {
          ...payload,
          source: 'MESSAGE',
        });
      }
      submitCallback?.();
    } finally {
      setIsDemandRequestOngoing(false);
    }
  };

  useEffect(() => {
    const updateSwimmingTour = () => {
      dispatch({
        payload: true,
        type: 'isSwimmingTour',
      });
    };

    eventBus.addEventListener(
      eventBus.events.SWIMMING_TOUR_CHANGE,
      updateSwimmingTour
    );
    return () => {
      eventBus.removeEventListener(
        eventBus.events.SWIMMING_TOUR_CHANGE,
        updateSwimmingTour
      );
    };
  }, []);

  const endHourOnNextDay = (() => {
    const isImmediateReservation = supportedDemandTypes.includes(
      SupportedDemandType.IMMEDIATE_RESERVATION
    );
    if (isImmediateReservation) {
      if (
        rentalInfo.operationalHours?.start === rentalInfo.operationalHours?.end
      ) {
        return 6;
      }
      if ((rentalInfo.operationalHours?.end || 6) >= 6) {
        return 6;
      } else {
        return (rentalInfo.operationalHours?.end || 0) + 1;
      }
    } else {
      return 6;
    }
  })();

  const formItemClasses = 'pb-2.5 pt-1 px-3';
  const formLabelClasses = 'text-sm font-semibold mb-1 inline-block';
  const formBorderColor = 'border-slate-500';

  return (
    <div>
      {rentalInfo.supportedRentalTypes.includes(
        SupportedHourlyRentalTypes.SWIMMING_TOUR
      ) ? (
        <div className="mb-2 mt-1 text-sm text-slate-500">
          <Info size={14} className="mr-1 mb-1 inline-block" />
          Bu tekne ile{' '}
          <span className="font-semibold text-primary-500">
            yüzme turu
          </span>{' '}
          yapabilirsiniz.{' '}
          <span className="font-semibold">
            Tarih seçerken yüzme turu seçeneğini işaretlemeyi
          </span>{' '}
          unutmayınız.
        </div>
      ) : null}
      <div
        className={classNames(
          'form mt-3 grid grid-cols-2 rounded-2xl border',
          formBorderColor
        )}
      >
        <div
          className={classNames(
            formItemClasses,
            formBorderColor,
            'border-b border-r'
          )}
        >
          <label className={formLabelClasses} htmlFor="harbor">
            {t('priceWidget.form.harbor')}
          </label>
          <select
            id="harbor"
            className={classNames(
              'naked-input block w-full text-center',
              selectStyles['b-select']
            )}
            placeholder="limansec"
            value={state.harbor}
            onChange={(event) => {
              dispatch({
                type: 'harbor',
                payload: event.target.value,
              });
              dispatch({
                type: 'endHarbor',
                payload: event.target.value,
              });
            }}
          >
            {harbors.map((harbor) => (
              <option
                value={harbor.value}
                disabled={
                  !!lockHarborsOtherThanThis &&
                  lockHarborsOtherThanThis !== harbor.value
                }
                key={harbor.value}
              >
                {harbor.label}
              </option>
            ))}
          </select>
        </div>
        <div
          className={classNames(formItemClasses, formBorderColor, 'border-b')}
        >
          <label className={formLabelClasses} htmlFor="datepicker">
            {t('priceWidget.form.date')}
          </label>
          <BDatePicker
            minDetail="year"
            next2Label={null}
            prev2Label={null}
            calendarClasses="!top-8 -right-3"
            disabledDates={mappedReservedTimes}
            inputClasses="text-center naked-input"
            minDate={new Date()}
            disabledHours={
              state.isSwimmingTour
                ? disabledHoursWhenSwimmingTourSelected
                : datePickerDisabledHours
            }
            showOnLeft
            showTimePicker
            endHourOnNextDay={endHourOnNextDay}
            value={state.date}
            onChange={(val) =>
              dispatch({
                type: 'date',
                payload: val,
              })
            }
          >
            <div className="px-2 pt-1 pb-2">
              <BSwitch
                value={state.isSwimmingTour}
                disabled={
                  doesVehicleSupportOnlySwimmingTour ||
                  doesVehicleNotSupportSwimmingTour
                }
                labelClasses="text-info-600"
                onChange={(val) =>
                  dispatch({
                    type: 'isSwimmingTour',
                    payload: val,
                  })
                }
                label={t('priceWidget.form.swimmingTour')}
              />
              {doesVehicleSupportOnlySwimmingTour ||
              doesVehicleNotSupportSwimmingTour ? (
                <span className="mt-1 inline-block text-sm text-slate-500">
                  {doesVehicleSupportOnlySwimmingTour
                    ? t('priceWidget.form.swimmingTourOnlySupported')
                    : t('priceWidget.form.swimmingTourNotSupported')}
                </span>
              ) : null}
              {}
            </div>
          </BDatePicker>
        </div>
        <div
          className={classNames(formItemClasses, formBorderColor, 'border-r')}
        >
          <label className={formLabelClasses} htmlFor="duration">
            {t('priceWidget.form.duration')}
          </label>
          <BStepperInput
            inputProps={{ id: 'duration' }}
            minLimit={MIN_DURATION}
            maxLimit={MAX_DURATION}
            suffix={t('priceWidget.form.HOUR')}
            value={state.duration}
            onChange={(value) =>
              dispatch({
                type: 'duration',
                payload: value,
              })
            }
          />
        </div>
        <div className={formItemClasses}>
          <label className={formLabelClasses} htmlFor="number-of-people">
            {t('priceWidget.form.personCount')}
          </label>
          <div className="relative">
            <BStepperInput
              inputProps={{ id: 'number-of-people' }}
              minLimit={MIN_PEOPLE}
              maxLimit={MAX_PEOPLE}
              value={state.numberOfPeople}
              inputEnabled
              boundaryErrorKey="vehicle:priceWidget.form.personCountBoundaryError"
              onChange={(value) =>
                dispatch({
                  type: 'numberOfPeople',
                  payload: value,
                })
              }
            />
          </div>
        </div>
      </div>
      <ModalWithActivator
        modalTitle={t('priceWidget.form.extrasModal.title')}
        activator={<PriceWidgetExtras />}
        modalClass="h-screen"
      >
        {extraTabs.length ? (
          <BTabs tabs={extraTabs} />
        ) : (
          <PriceWidgetExtrasEmptyView />
        )}
      </ModalWithActivator>
      {state.isSwimmingTour ? (
        <BAlert variant="info" className="mt-4">
          <Trans i18nKey="vehicle:priceWidget.form.alerts.swimmingTour" />
        </BAlert>
      ) : null}
      {submitType === 'default' &&
      lockHarborsOtherThanThis &&
      harbors.length > 1 ? (
        <BAlert variant="secondary" className="mt-4">
          {state.harbor !== state.endHarbor ? (
            <Trans
              i18nKey="vehicle:priceWidget.form.alerts.endHarborChangedDueRezervations"
              values={{
                harbor: harbors.find((h) => +state.endHarbor === h.value)
                  ?.label,
              }}
              components={[
                <strong className="text-primary-700" key={'harborEmphasis'} />,
              ]}
            />
          ) : (
            t('priceWidget.form.alerts.harborsDisabledDueReservation')
          )}
        </BAlert>
      ) : null}
      {submitType === 'message' ? (
        <BTextArea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-4"
        />
      ) : null}
      {submitType === 'default' ? (
        <PriceWidgetCTAButton
          className="mt-4"
          fluid
          onClick={onSubmit}
          supportedDemandTypes={supportedDemandTypes}
          pricing={pricing}
          isCalculateAmountRequestSent={isCalculateAmountRequestSent}
          isCalcutePricingRequestOngoing={isCalcutePricingRequestOngoing}
          isDemandRequestOngoing={isDemandRequestOngoing}
        />
      ) : (
        <BButton
          className="mt-4"
          fluid
          onClick={onSubmit}
          disabled={isDemandRequestOngoing}
        >
          {isDemandRequestOngoing ? (
            <BSpinnerInline size={24} className="mx-auto" />
          ) : submitType === 'message' ? (
            t('message.cta')
          ) : (
            t('update.cta')
          )}
        </BButton>
      )}
    </div>
  );
};

export default PriceWidgetForm;
